import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { login, requestMagicLink } from '../../services/auth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth();

  const [mode, setMode] = useState<'password' | 'magic'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      loginSuccess(result.user, result.organization, result.tokens);
      void navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await requestMagicLink(email);
      setMagicLinkSent(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send login link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="5" r="3"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="12" y1="16" x2="8" y2="22"/>
              <line x1="12" y1="16" x2="16" y2="22"/>
              <circle cx="12" cy="11" r="1"/>
              <circle cx="12" cy="14" r="1"/>
            </svg>
          </div>
          <h1 className="login-title">Data Bodies</h1>
          <p className="login-subtitle">Sign in to your account</p>
        </div>

        {magicLinkSent ? (
          <div className="magic-link-sent">
            <div className="magic-link-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M22 4L12 13L2 4"/>
              </svg>
            </div>
            <h2>Check your email</h2>
            <p>We sent a login link to <strong>{email}</strong>. Click the link in the email to sign in.</p>
            <button className="btn btn-secondary" onClick={() => { setMagicLinkSent(false); setMode('password'); }}>
              Back to login
            </button>
          </div>
        ) : (
          <>
            <div className="login-tabs">
              <button
                className={`login-tab ${mode === 'password' ? 'active' : ''}`}
                onClick={() => setMode('password')}
              >
                Password
              </button>
              <button
                className={`login-tab ${mode === 'magic' ? 'active' : ''}`}
                onClick={() => setMode('magic')}
              >
                Email Link
              </button>
            </div>

            {error && <div className="login-error">{error}</div>}

            {mode === 'password' ? (
              <form onSubmit={handlePasswordLogin} className="login-form">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleMagicLink} className="login-form">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoFocus
                  />
                </div>
                <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Login Link'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
